import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Paper,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import ownAuthSession from '../../../services/auth/ownAuthSession';

interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceType: string;
  accountName: string;
  grandTotal: number;
  currency: string;
  invoiceDate: string;
  notes?: string;
}

interface RelatedAccountTransaction {
  id: number;
  accountId: number;
  accountName: string;
  transactionType: string;
  description: string;
  debitAmount: number;
  creditAmount: number;
  balanceAfter: number;
  transactionDate: string;
  paymentMethod?: string;
}

interface RelatedStockMovement {
  id: string;
  productId: string;
  productName: string;
  warehouseId: string;
  movementType: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  description: string;
  movementDate: string;
}

interface RelatedCashboxTransaction {
  id: number;
  cashboxId: number;
  cashboxName: string;
  transactionType: string;
  amount: number;
  balanceAfter: number;
  description: string;
  transactionDate: string;
}

interface RelatedBankTransaction {
  id: number;
  bankAccountId: number;
  bankAccountName: string;
  transactionType: string;
  amount: number;
  balanceAfter: number;
  description: string;
  transactionDate: string;
}

interface RelatedQuickSaleReceipt {
  id: number;
  receiptNumber: string;
  invoiceNumber?: string;
  accountName: string;
  totalAmount: number;
  paymentType: string;
  paymentStatus: string;
  paymentCount: number;
  isFiscalReceipt: boolean;
  receiptDate: string;
}

interface RelatedTransactionsSummary {
  totalAccountTransactions: number;
  totalStockMovements: number;
  totalCashboxTransactions: number;
  totalBankTransactions: number;
  totalQuickSaleReceipts: number;
  totalQuickSaleReceiptPayments: number;
  totalDebitAmount: number;
  totalCreditAmount: number;
  totalStockQuantity: number;
  totalCashAmount: number;
  totalBankAmount: number;
  totalQuickSaleAmount: number;
}

interface RelatedTransactions {
  accountTransactions: RelatedAccountTransaction[];
  stockMovements: RelatedStockMovement[];
  cashboxTransactions: RelatedCashboxTransaction[];
  bankTransactions: RelatedBankTransaction[];
  quickSaleReceipts: RelatedQuickSaleReceipt[];
  summary: RelatedTransactionsSummary;
}

interface InvoiceDeleteConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmDelete?: () => void;
  invoiceId: string;
  companyId: string | null; // ✅ YENİ: Çok şirketli sistem için ZORUNLU
  extraWarning?: string; // Opsiyonel ek uyarı metni (örn. PavoPay e-belge bilgilendirmesi)
}

const InvoiceDeleteConfirmModal: React.FC<InvoiceDeleteConfirmModalProps> = ({
  open,
  onClose,
  onConfirmDelete,
  invoiceId,
  companyId, // ✅ YENİ: Çok şirketli sistem için
  extraWarning,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [relatedTransactions, setRelatedTransactions] = useState<RelatedTransactions | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open && invoiceId && companyId) { // ✅ companyId kontrolü eklendi
      loadRelatedTransactions();
    }
  }, [open, invoiceId, companyId]); // ✅ companyId dependency eklendi

  const loadRelatedTransactions = async () => {
    // ✅ YENİ: CompanyId kontrolü - GÜVENLİK
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı. Güvenlik nedeniyle işlem yapılamıyor.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // ✅ YENİ: CompanyId'yi query string olarak ekle
      const response = await accountingApi.get(
        `${ACCOUNTING_API.INVOICE_RELATED_TRANSACTIONS(invoiceId)}`
      );
      const result = response.data;
      if (result.success) {
        setInvoice(result.data.invoice);
        setRelatedTransactions(result.data.relatedTransactions);
      } else {
        throw new Error(result.message || 'Fatura hareketleri yüklenemedi');
      }
    } catch (err: any) {
      console.error('❌ [GÜVENLİK] Fatura hareketleri yüklenirken hata:', err);
      setError(err.message || 'Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getInvoiceTypeText = (type: string) => {
    switch (type?.toUpperCase()) {
      case 'SALES':
        return 'Satış Faturası';
      case 'PURCHASE':
        return 'Alış Faturası';
      case 'QUICK_SALE':
        return 'Hızlı Satış (Kapalı)';
      default:
        return type;
    }
  };

  const handleDelete = async () => {
    if (!invoice) return;
    
    // ✅ YENİ: CompanyId kontrolü - GÜVENLİK
    if (!companyId) {
      setError('Şirket bilgisi bulunamadı. Güvenlik nedeniyle işlem yapılamıyor.');
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        throw new Error('Kullanıcı oturumu bulunamadı');
      }

      // ✅ YENİ: POST method ile body'de parametreleri gönder (DELETE çalışmıyor)
      const response = await accountingApi.post(
        ACCOUNTING_API.INVOICE_DELETE(invoiceId),
        {
          companyId,
          deletedBy: ownAuthSession.getCurrentEmail() || uid
        }
      );

      const result = response.data;
      if (result.success) {
        console.log('✅ [GÜVENLİK] Fatura silindi - CompanyId:', companyId, 'InvoiceId:', invoiceId);
        // Başarılı - Parent component'e bildir
        if (onConfirmDelete) {
          onConfirmDelete();
        }
        onClose();
      } else {
        throw new Error(result.message || 'Fatura silinemedi');
      }
    } catch (err: any) {
      console.error('❌ [GÜVENLİK] Fatura silinirken hata:', err.response?.status, err.response?.data);
      const apiError = err.response?.data?.error || err.response?.data?.message || err.message;
      setError(apiError || 'Fatura silinirken bir hata oluştu');
    } finally {
      setDeleting(false);
    }
  };

  const totalTransactions = relatedTransactions 
    ? relatedTransactions.summary.totalAccountTransactions +
      relatedTransactions.summary.totalStockMovements +
      relatedTransactions.summary.totalCashboxTransactions +
      relatedTransactions.summary.totalBankTransactions +
      relatedTransactions.summary.totalQuickSaleReceipts +
      relatedTransactions.summary.totalQuickSaleReceiptPayments
    : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle sx={{ 
        bgcolor: 'error.main', 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <WarningIcon fontSize="large" />
        <Box>
          <Typography variant="h6">Fatura Silme Onayı</Typography>
          {invoice && (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {invoice.invoiceNumber} - {getInvoiceTypeText(invoice.invoiceType)}
            </Typography>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Özet Bilgi */}
            {invoice && (
              <Paper elevation={2} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Fatura No:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoice.invoiceNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Cari Hesap:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {invoice.accountName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Toplam Tutar:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatCurrency(invoice.grandTotal, invoice.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">
                      Tarih:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatDate(invoice.invoiceDate)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Ek Uyarı (örn. PavoPay e-belge bilgilendirmesi) */}
            {extraWarning && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">{extraWarning}</Typography>
              </Alert>
            )}

            {/* Uyarı Mesajı */}
            <Alert severity="warning" icon={<InfoIcon />} sx={{ mb: 3 }}>
              <Typography variant="body2" gutterBottom>
                <strong>DİKKAT:</strong> Bu faturayı sildiğinizde, aşağıdaki <strong>{totalTransactions} adet</strong> hareket kaydı da silinecek ve ilgili bakiyeler güncellenecektir.
                {relatedTransactions && relatedTransactions.summary.totalQuickSaleReceipts > 0 && (
                  <> Bu faturaya bağlı <strong>satış kaydı / gün sonu</strong> fişleri de kalıcı olarak silinecektir.</>
                )}
              </Typography>
            </Alert>

            {/* Hareketler Özeti */}
            {relatedTransactions && (
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  {/* Cari Hareketler */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        bgcolor: relatedTransactions.summary.totalAccountTransactions > 0 ? 'warning.light' : 'grey.100',
                        color: relatedTransactions.summary.totalAccountTransactions > 0 ? 'warning.dark' : 'text.secondary'
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {relatedTransactions.summary.totalAccountTransactions}
                      </Typography>
                      <Typography variant="caption">
                        Cari Hareket
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Stok Hareketleri */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        bgcolor: relatedTransactions.summary.totalStockMovements > 0 ? 'warning.light' : 'grey.100',
                        color: relatedTransactions.summary.totalStockMovements > 0 ? 'warning.dark' : 'text.secondary'
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {relatedTransactions.summary.totalStockMovements}
                      </Typography>
                      <Typography variant="caption">
                        Stok Hareket
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Kasa Hareketleri */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        bgcolor: relatedTransactions.summary.totalCashboxTransactions > 0 ? 'warning.light' : 'grey.100',
                        color: relatedTransactions.summary.totalCashboxTransactions > 0 ? 'warning.dark' : 'text.secondary'
                      }}
                    >
                      <AccountBalanceWalletIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {relatedTransactions.summary.totalCashboxTransactions}
                      </Typography>
                      <Typography variant="caption">
                        Kasa Hareket
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Banka Hareketleri */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        bgcolor: relatedTransactions.summary.totalBankTransactions > 0 ? 'warning.light' : 'grey.100',
                        color: relatedTransactions.summary.totalBankTransactions > 0 ? 'warning.dark' : 'text.secondary'
                      }}
                    >
                      <AccountBalanceIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {relatedTransactions.summary.totalBankTransactions}
                      </Typography>
                      <Typography variant="caption">
                        Banka Hareket
                      </Typography>
                    </Paper>
                  </Grid>

                  {/* Hızlı Satış Fişleri (Gün Sonu / Satış Kaydı) */}
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper 
                      elevation={1} 
                      sx={{ 
                        p: 2, 
                        textAlign: 'center',
                        bgcolor: relatedTransactions.summary.totalQuickSaleReceipts > 0 ? 'warning.light' : 'grey.100',
                        color: relatedTransactions.summary.totalQuickSaleReceipts > 0 ? 'warning.dark' : 'text.secondary'
                      }}
                    >
                      <ReceiptLongIcon sx={{ fontSize: 40, mb: 1 }} />
                      <Typography variant="h4" fontWeight="bold">
                        {relatedTransactions.summary.totalQuickSaleReceipts}
                      </Typography>
                      <Typography variant="caption">
                        Satış Kaydı / Gün Sonu
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Detaylı Hareketler */}
            {relatedTransactions && (
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                  Hareket Detayları
                </Typography>

                {/* Cari Hareketler Accordion */}
                {relatedTransactions.accountTransactions.length > 0 && (
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <PersonIcon color="primary" />
                        <Typography fontWeight="bold">
                          Cari Hareketler ({relatedTransactions.accountTransactions.length})
                        </Typography>
                        <Chip 
                          label={`Borç: ${formatCurrency(relatedTransactions.summary.totalDebitAmount)} | Alacak: ${formatCurrency(relatedTransactions.summary.totalCreditAmount)}`}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Cari Hesap</TableCell>
                            <TableCell>İşlem Tipi</TableCell>
                            <TableCell align="right">Borç</TableCell>
                            <TableCell align="right">Alacak</TableCell>
                            <TableCell align="right">Bakiye</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {relatedTransactions.accountTransactions.map((at) => (
                            <TableRow key={at.id}>
                              <TableCell>{at.accountName}</TableCell>
                              <TableCell>
                                <Chip label={at.transactionType} size="small" variant="outlined" />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(at.debitAmount)}</TableCell>
                              <TableCell align="right">{formatCurrency(at.creditAmount)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(at.balanceAfter)}
                              </TableCell>
                              <TableCell>{formatDate(at.transactionDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Stok Hareketleri Accordion */}
                {relatedTransactions.stockMovements.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <InventoryIcon color="secondary" />
                        <Typography fontWeight="bold">
                          Stok Hareketleri ({relatedTransactions.stockMovements.length})
                        </Typography>
                        <Chip 
                          label={`Toplam: ${relatedTransactions.summary.totalStockQuantity.toFixed(2)} Adet`}
                          size="small"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Ürün Adı</TableCell>
                            <TableCell>Hareket Tipi</TableCell>
                            <TableCell align="right">Miktar</TableCell>
                            <TableCell align="right">Birim Fiyat</TableCell>
                            <TableCell align="right">Toplam</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {relatedTransactions.stockMovements.map((sm) => (
                            <TableRow key={sm.id}>
                              <TableCell>{sm.productName}</TableCell>
                              <TableCell>
                                <Chip label={sm.movementType} size="small" variant="outlined" color="secondary" />
                              </TableCell>
                              <TableCell align="right">{sm.quantity.toFixed(2)}</TableCell>
                              <TableCell align="right">{formatCurrency(sm.unitPrice)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(sm.totalPrice)}
                              </TableCell>
                              <TableCell>{formatDate(sm.movementDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Kasa Hareketleri Accordion */}
                {relatedTransactions.cashboxTransactions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <AccountBalanceWalletIcon color="success" />
                        <Typography fontWeight="bold">
                          Kasa Hareketleri ({relatedTransactions.cashboxTransactions.length})
                        </Typography>
                        <Chip 
                          label={formatCurrency(relatedTransactions.summary.totalCashAmount)}
                          size="small"
                          color="success"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kasa Adı</TableCell>
                            <TableCell>İşlem Tipi</TableCell>
                            <TableCell align="right">Tutar</TableCell>
                            <TableCell align="right">Bakiye</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {relatedTransactions.cashboxTransactions.map((ct) => (
                            <TableRow key={ct.id}>
                              <TableCell>{ct.cashboxName}</TableCell>
                              <TableCell>
                                <Chip label={ct.transactionType} size="small" variant="outlined" color="success" />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(ct.amount)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(ct.balanceAfter)}
                              </TableCell>
                              <TableCell>{formatDate(ct.transactionDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Banka Hareketleri Accordion */}
                {relatedTransactions.bankTransactions.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <AccountBalanceIcon color="info" />
                        <Typography fontWeight="bold">
                          Banka Hareketleri ({relatedTransactions.bankTransactions.length})
                        </Typography>
                        <Chip 
                          label={formatCurrency(relatedTransactions.summary.totalBankAmount)}
                          size="small"
                          color="info"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Banka Hesabı</TableCell>
                            <TableCell>İşlem Tipi</TableCell>
                            <TableCell align="right">Tutar</TableCell>
                            <TableCell align="right">Bakiye</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {relatedTransactions.bankTransactions.map((bt) => (
                            <TableRow key={bt.id}>
                              <TableCell>{bt.bankAccountName}</TableCell>
                              <TableCell>
                                <Chip label={bt.transactionType} size="small" variant="outlined" color="info" />
                              </TableCell>
                              <TableCell align="right">{formatCurrency(bt.amount)}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(bt.balanceAfter)}
                              </TableCell>
                              <TableCell>{formatDate(bt.transactionDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Hızlı Satış Fişleri (Gün Sonu / Satış Kaydı) Accordion */}
                {relatedTransactions.quickSaleReceipts.length > 0 && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <ReceiptLongIcon color="warning" />
                        <Typography fontWeight="bold">
                          Satış Kaydı / Gün Sonu ({relatedTransactions.quickSaleReceipts.length})
                        </Typography>
                        <Chip 
                          label={`${relatedTransactions.summary.totalQuickSaleReceiptPayments} ödeme | ${formatCurrency(relatedTransactions.summary.totalQuickSaleAmount)}`}
                          size="small"
                          color="warning"
                          sx={{ ml: 'auto' }}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Fiş No</TableCell>
                            <TableCell>Cari</TableCell>
                            <TableCell>Ödeme Tipi</TableCell>
                            <TableCell align="center">Ödeme Sayısı</TableCell>
                            <TableCell align="right">Tutar</TableCell>
                            <TableCell>Tarih</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {relatedTransactions.quickSaleReceipts.map((qsr) => (
                            <TableRow key={qsr.id}>
                              <TableCell>{qsr.receiptNumber}</TableCell>
                              <TableCell>{qsr.accountName}</TableCell>
                              <TableCell>
                                <Chip label={qsr.paymentType} size="small" variant="outlined" color="warning" />
                              </TableCell>
                              <TableCell align="center">{qsr.paymentCount}</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                {formatCurrency(qsr.totalAmount)}
                              </TableCell>
                              <TableCell>{formatDate(qsr.receiptDate)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </AccordionDetails>
                  </Accordion>
                )}

                {/* Hiç hareket yoksa */}
                {totalTransactions === 0 && (
                  <Alert severity="info">
                    Bu faturaya bağlı herhangi bir hareket kaydı bulunamadı. Faturayı güvenle silebilirsiniz.
                  </Alert>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" disabled={deleting}>
          İptal
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteForeverIcon />}
          disabled={loading || deleting}
        >
          {deleting ? 'Siliniyor...' : 'Faturayı ve Tüm Hareketleri Sil'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InvoiceDeleteConfirmModal;

